import React, { useEffect, useRef, useCallback } from 'react';
import { Button, Tooltip, Select, Divider, ColorPicker } from 'antd';
import {
  BoldOutlined, ItalicOutlined, UnderlineOutlined, StrikethroughOutlined,
  OrderedListOutlined, UnorderedListOutlined, LinkOutlined, ClearOutlined,
  AlignLeftOutlined, AlignCenterOutlined, AlignRightOutlined,
} from '@ant-design/icons';

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

/**
 * 의존성 없는 경량 리치 텍스트 에디터 (contentEditable 기반).
 * - 외부 npm 패키지 불필요 → React 19 호환성 문제 없음
 * - 본문 HTML을 그대로 value/onChange 로 주고받음 (서버에서 정화 처리)
 */
const RichTextEditor: React.FC<Props> = ({ value, onChange, placeholder = '내용을 입력하세요…', minHeight = 280 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isComposing = useRef(false);

  // 외부 value 가 에디터 내용과 다를 때만 주입 (타이핑 중 커서 점프 방지)
  useEffect(() => {
    const el = ref.current;
    if (el && el.innerHTML !== value) {
      el.innerHTML = value || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = useCallback(() => {
    if (ref.current) onChange(ref.current.innerHTML);
  }, [onChange]);

  const exec = (command: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const setBlock = (tag: string) => exec('formatBlock', tag);

  const insertLink = () => {
    const url = window.prompt('링크 URL을 입력하세요 (https://...)');
    if (url) {
      const safe = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      exec('createLink', safe);
    }
  };

  const btn = (icon: React.ReactNode, title: string, onClick: () => void) => (
    <Tooltip title={title}>
      <Button type="text" size="small" icon={icon} onMouseDown={(e) => e.preventDefault()} onClick={onClick} />
    </Tooltip>
  );

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
      {/* 툴바 */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2,
        padding: '6px 8px', borderBottom: '1px solid #f0f0f0', background: '#fafafa',
      }}>
        <Select
          size="small" defaultValue="p" style={{ width: 96 }} variant="borderless"
          onChange={(v) => setBlock(v)}
          options={[
            { value: 'p', label: '본문' },
            { value: 'h1', label: '제목 1' },
            { value: 'h2', label: '제목 2' },
            { value: 'h3', label: '제목 3' },
            { value: 'blockquote', label: '인용' },
          ]}
        />
        <Divider type="vertical" style={{ margin: '0 4px' }} />
        {btn(<BoldOutlined />, '굵게', () => exec('bold'))}
        {btn(<ItalicOutlined />, '기울임', () => exec('italic'))}
        {btn(<UnderlineOutlined />, '밑줄', () => exec('underline'))}
        {btn(<StrikethroughOutlined />, '취소선', () => exec('strikeThrough'))}
        <ColorPicker
          size="small"
          onChangeComplete={(c) => exec('foreColor', c.toHexString())}
          presets={[{ label: '기본', colors: ['#000000', '#4f46e5', '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#64748b'] }]}
        >
          <Tooltip title="글자색"><Button type="text" size="small" onMouseDown={(e) => e.preventDefault()}>가</Button></Tooltip>
        </ColorPicker>
        <Divider type="vertical" style={{ margin: '0 4px' }} />
        {btn(<UnorderedListOutlined />, '글머리 목록', () => exec('insertUnorderedList'))}
        {btn(<OrderedListOutlined />, '번호 목록', () => exec('insertOrderedList'))}
        {btn(<AlignLeftOutlined />, '왼쪽 정렬', () => exec('justifyLeft'))}
        {btn(<AlignCenterOutlined />, '가운데 정렬', () => exec('justifyCenter'))}
        {btn(<AlignRightOutlined />, '오른쪽 정렬', () => exec('justifyRight'))}
        <Divider type="vertical" style={{ margin: '0 4px' }} />
        {btn(<LinkOutlined />, '링크', insertLink)}
        {btn(<ClearOutlined />, '서식 지우기', () => exec('removeFormat'))}
      </div>

      {/* 편집 영역 */}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={() => { if (!isComposing.current) emit(); }}
        onCompositionStart={() => { isComposing.current = true; }}
        onCompositionEnd={() => { isComposing.current = false; emit(); }}
        onBlur={emit}
        className="rte-editable"
        style={{ minHeight, padding: '12px 14px', outline: 'none', fontSize: 14, lineHeight: 1.7 }}
      />
      <style>{`
        .rte-editable:empty:before { content: attr(data-placeholder); color: #bfbfbf; }
        .rte-editable img { max-width: 100%; }
        .rte-editable blockquote {
          margin: 8px 0; padding: 6px 14px; border-left: 3px solid #d9d9d9; color: #595959;
        }
        .rte-editable h1 { font-size: 1.6em; margin: .4em 0; }
        .rte-editable h2 { font-size: 1.35em; margin: .4em 0; }
        .rte-editable h3 { font-size: 1.15em; margin: .4em 0; }
        .rte-editable a { color: #4f46e5; }
        .rte-editable ul, .rte-editable ol { padding-left: 22px; }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
